import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { DateRangePickerModalComponent, DateRangePickerComponent } from '@softline/ui-core/date-input';
import { ModalService } from '@softline/ui-core';
import { DateRange } from '@softline/ui-core';
import { ResolvableModalConfig } from '@softline/ui-core';
import { DateRangeFieldInput } from '../date-range-input/date-range-field-input';
import { DateRangeFromInputComponent } from '../date-range-input/date-range-from-input/date-range-from-input.component';
import { DateRangeInputPickerTrigger } from '../date-range-input/date-range-input.types';
import { DateRangeToInputComponent } from '../date-range-input/date-range-to-input/date-range-to-input.component';
import { LabelComponent } from '@softline/ui-core';
import { OverlayComponent } from '@softline/ui-core';
import { DateRangeFieldPresentation, DateRangeFieldSize } from './date-range-field.types';

let nextDateRangeFieldId = 0;

@Component({
  selector: 'soft-ds-date-range-field',
  imports: [
    CdkOverlayOrigin,
    DateRangeFromInputComponent,
    DateRangeToInputComponent,
    DateRangePickerComponent,
    LabelComponent,
    OverlayComponent,
  ],
  templateUrl: './date-range-field.component.html',
  styleUrl: './date-range-field.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeFieldComponent),
      multi: true,
    },
  ],
  host: {
    '[class]': 'hostClasses()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeFieldComponent implements ControlValueAccessor {
  private readonly controlContainer = inject(ControlContainer, { optional: true });
  private readonly modalService = inject(ModalService);
  private readonly fieldId = nextDateRangeFieldId++;

  private onChange: (value: DateRange | null) => void = () => void 0;
  private onTouched: () => void = () => void 0;

  readonly value = model<DateRange | null>(null);
  readonly from = model<string | null>(null);
  readonly to = model<string | null>(null);
  readonly open = model(false);
  readonly touched = model(false);

  readonly fromControl = input<AbstractControl<string | null> | null>(null);
  readonly toControl = input<AbstractControl<string | null> | null>(null);
  readonly fromControlName = input<string | null>(null);
  readonly toControlName = input<string | null>(null);

  readonly presentation = input<DateRangeFieldPresentation>('dropdown');
  readonly size = input<DateRangeFieldSize>('lg');
  readonly fromLabel = input<string | null>('Von');
  readonly toLabel = input<string | null>('Bis');
  readonly placeholder = input<string>('Datum eingeben');
  readonly format = input<string>('DATE.DEFAULT');
  readonly changeTrigger = input<'blur' | 'input'>('blur');
  readonly showShortcuts = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly showWeekNumbers = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly showRangeLabels = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly disabledInput = input<boolean, unknown>(false, {
    // eslint-disable-next-line @angular-eslint/no-input-rename -- Preserves the public `disabled` input while keeping the internal signal distinct.
    alias: 'disabled',
    transform: booleanAttribute,
  });
  readonly readonly = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });

  protected readonly formDisabled = signal(false);
  protected readonly fromValue = signal<string | null>(null);
  protected readonly toValue = signal<string | null>(null);
  protected readonly activeField = signal<'from' | 'to'>('from');
  protected readonly fromInputId = `soft-ds-date-range-field-${this.fieldId}-from`;
  protected readonly toInputId = `soft-ds-date-range-field-${this.fieldId}-to`;

  protected readonly isDisabled = computed(
    () => this.disabledInput() || this.formDisabled()
  );
  protected readonly endpointPickerTrigger = computed<DateRangeInputPickerTrigger>(
    () => 'manual'
  );
  protected readonly overlayWidth = computed(() => {
    if (this.showShortcuts() && this.showWeekNumbers()) {
      return 'var(--ds-date-range-field-overlay-width-with-shortcuts-and-week-numbers)';
    }

    if (this.showShortcuts()) {
      return 'var(--ds-date-range-field-overlay-width-with-shortcuts)';
    }

    if (this.showWeekNumbers()) {
      return 'var(--ds-date-range-field-overlay-width-with-week-numbers)';
    }

    return 'var(--ds-date-range-field-overlay-width)';
  });
  protected readonly currentRange = computed<DateRange | null>(() =>
    this.toDateRange(this.fromValue(), this.toValue())
  );
  protected readonly hasValue = computed(
    () => Boolean(this.fromValue()) || Boolean(this.toValue())
  );

  protected readonly hostClasses = computed(
    () =>
      `soft-ds-date-range-field-host size-${this.size()} presentation-${this.presentation()}` +
      (this.showWeekNumbers() ? ' has-week-numbers' : '') +
      (this.open() ? ' is-open' : '') +
      (this.hasValue() ? ' is-filled' : '') +
      (this.isDisabled() ? ' is-disabled' : '') +
      (this.readonly() ? ' readonly is-readonly' : '')
  );

  private readonly fromControlRef = computed(
    () => this.fromControl() ?? this.resolveNamedControl(this.fromControlName())
  );
  private readonly toControlRef = computed(
    () => this.toControl() ?? this.resolveNamedControl(this.toControlName())
  );

  private readonly sourceSyncEffect = effect((onCleanup) => {
    const fromControl = this.fromControlRef();
    const toControl = this.toControlRef();

    this.syncFromSources(fromControl, toControl);

    const subscriptions = new Subscription();
    if (fromControl) {
      subscriptions.add(
        fromControl.valueChanges.subscribe((value) => this.fromValue.set(value ?? null))
      );
    }
    if (toControl) {
      subscriptions.add(
        toControl.valueChanges.subscribe((value) => this.toValue.set(value ?? null))
      );
    }

    onCleanup(() => subscriptions.unsubscribe());
  });

  registerOnChange(fn: (value: DateRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    this.commitValue(this.normalizeDateRange(value), false);
  }

  protected onEndpointValueChange(field: 'from' | 'to', value: string | null): void {
    const from = field === 'from' ? value : this.fromValue();
    const to = field === 'to' ? value : this.toValue();
    this.commitParts(from, to, true);
  }

  protected onEndpointBlur(): void {
    this.markTouched();
  }

  protected async onEndpointPickerToggle(field: 'from' | 'to'): Promise<void> {
    if (this.presentation() === 'dropdown' && this.open() && this.activeField() === field) {
      this.markTouched();
      this.open.set(false);
      return;
    }
    await this.onEndpointPickerOpen(field);
  }

  protected async onEndpointPickerOpen(field: 'from' | 'to'): Promise<void> {
    this.markTouched();
    this.activeField.set(field);

    if (this.presentation() === 'modal') {
      await this.openModalPicker(field);
      return;
    }

    this.open.set(true);
  }

  protected async onEndpointLabelClick(
    event: MouseEvent,
    endpoint: DateRangeFieldInput
  ): Promise<void> {
    event.preventDefault();
    await endpoint.focusAndOpenPicker();
  }

  protected onPickerValueChange(value: DateRange | null): void {
    this.commitValue(value, true);
    this.open.set(false);
  }

  private async openModalPicker(field: 'from' | 'to'): Promise<void> {
    const modalConfig: ResolvableModalConfig<
      DateRangePickerModalComponent,
      DateRange | null
    > = {
      component: DateRangePickerModalComponent,
      dismiss: true,
      priority: Number.POSITIVE_INFINITY,
      class: 'soft-ds-date-range-picker-modal',
      data: {
        value: this.currentRange(),
        startField: field,
      },
    };

    const result = await this.modalService.open(modalConfig);
    if (!result || result === 'DISMISSED') return;

    this.commitValue(result, true);
  }

  private commitValue(value: DateRange | null, emitChange: boolean): void {
    this.commitParts(value?.from ?? null, value?.to ?? null, emitChange);
  }

  private commitParts(from: string | null, to: string | null, emitChange: boolean): void {
    const nextValue = this.toDateRange(from, to);

    this.fromValue.set(from);
    this.toValue.set(to);
    this.value.set(nextValue);
    this.from.set(from);
    this.to.set(to);
    this.writeSplitControl(this.fromControlRef(), from);
    this.writeSplitControl(this.toControlRef(), to);

    if (emitChange) {
      this.onChange(nextValue);
    }
  }

  private syncFromSources(
    fromControl: AbstractControl<string | null> | null,
    toControl: AbstractControl<string | null> | null
  ): void {
    const range = this.value();
    const nextFrom = fromControl?.value ?? range?.from ?? this.from();
    const nextTo = toControl?.value ?? range?.to ?? this.to();

    untracked(() => {
      this.fromValue.set(nextFrom ?? null);
      this.toValue.set(nextTo ?? null);
    });
  }

  private writeSplitControl(
    control: AbstractControl<string | null> | null,
    value: string | null
  ): void {
    if (!control || control.value === value) return;
    control.setValue(value);
  }

  private resolveNamedControl(
    controlName: string | null
  ): AbstractControl<string | null> | null {
    if (!controlName) return null;
    return (
      (this.controlContainer?.control?.get(controlName) as AbstractControl<
        string | null
      > | null) ?? null
    );
  }

  private normalizeDateRange(value: unknown): DateRange | null {
    if (!value || typeof value !== 'object') return null;
    const maybeRange = value as Partial<DateRange>;
    if (!maybeRange.from || !maybeRange.to) return null;
    return { from: maybeRange.from, to: maybeRange.to };
  }

  private toDateRange(from: string | null, to: string | null): DateRange | null {
    if (!from || !to) return null;
    return { from, to };
  }

  private markTouched(): void {
    this.touched.set(true);
    this.onTouched();
  }
}
